import queryString from 'query-string';
import { notifyErrors } from 'shared/helpers/logger';
import { isErrorOAuth } from 'shared/helpers/errorHelper';
import { APP_VERSION } from 'shared/helpers/userAgentHelper';
import { getDeviceId } from 'shared/helpers/deviceIdHelper';
import getToken from 'shared/helpers/api/getToken';

function urlWithParams(url, params = {}) {
  const paramUrl = queryString.stringify(params);
  const newUrl = `${url}?${paramUrl}`;
  return newUrl;
}

function urlByMethod(method, url, data) {
  return method === 'get' ? urlWithParams(url, data) : url;
}

/* istanbul ignore next */
function request(ctx, method, endpointUrl, options = {}) {
  return new Promise((resolve, reject) => {
    ctx[method](endpointUrl, 'user', options.payload)
      .then(resolve)
      .catch(error => {
        if (isErrorOAuth(error)) {
          notifyErrors('SMAUG_OAUTH', error);
          if (process.env.NODE_ENV !== 'development' && window.localStorage.getItem('bl_token')) {
            window.localStorage.removeItem('bl_token');
          }
        }
        reject(error);
      });
  });
}

function generateRequestCustomSegment(method, url, options, data) {
  const segment = data.customSegments.reduce((a, s) => `${a}/${s}`);
  delete data.customSegments;
  const newUrl = `${url}/${segment}`;
  return urlByMethod(method, newUrl, data);
}

function generateRequestWithData(method, url, options, data) {
  let newUrl = url;
  if (options.appendUrl) {
    newUrl = `${newUrl}/${options.appendUrl}`;
  }
  return urlByMethod(method, newUrl, data);
}

function generateRequestWithId(method, url, options, data) {
  let newUrl = `${url}/${data}`;
  if (options.appendUrl) {
    newUrl = `${newUrl}/${options.appendUrl}`;
  }
  return newUrl;
}

function generateRequestWithIdAndData(method, url, options, data) {
  let newUrl = `${url}/${data.id}`;
  if (options.appendUrl) {
    newUrl = `${newUrl}/${options.appendUrl}`;
  }
  return urlByMethod(method, newUrl, data);
}

function isCustomSegment(options) {
  return options.withData && options.customSegments;
}

function isWithIdAndData(options) {
  return options.withId && options.withData;
}

export function generateApiUrl(method, url, options, data) {
  // DO NOT CHANGE ORDER
  if (isCustomSegment(options)) {
    return generateRequestCustomSegment(method, url, options, data);
  }
  if (isWithIdAndData(options)) {
    return generateRequestWithIdAndData(method, url, options, data);
  }
  if (options.withData) {
    return generateRequestWithData(method, url, options, data);
  }
  if (options.withId) {
    return generateRequestWithId(method, url, options, data);
  }
  return url;
}

function generateApiInterface(method, url, requestOptions, options) {
  /* istanbul ignore next */
  return function apiInterface(data, opts = {}) {
    const newUrl = generateApiUrl(method, url, options, data);

    let { headers } = opts;
    if (APP_VERSION) {
      headers = {
        'Bukalapak-App-Version': APP_VERSION,
        ...headers,
      };
    }

    if (options.withAuthorization) {
      headers = {
        Authorization: `Bearer ${JSON.parse(getToken()).access_token}`,
        ...headers,
      };
    }

    requestOptions.payload = { ...requestOptions.payload, headers };

    if (options.withData) {
      requestOptions.payload = { ...requestOptions.payload, data };
    }

    if (options.withCredentials) {
      requestOptions.payload = { ...requestOptions.payload, withCredentials: true };
    }

    return request(this, method, newUrl, requestOptions);
  };
}

function generateApiInterfaceAsync(method, url, requestOptions, options) {
  /* istanbul ignore next */
  return async function apiInterface(data, opts = {}) {
    const newUrl = generateApiUrl(method, url, options, data);

    let { headers } = opts;
    if (APP_VERSION) {
      headers = {
        'Bukalapak-App-Version': APP_VERSION,
        ...headers,
      };
    }

    if (options.withAuthorization) {
      headers = {
        Authorization: `Bearer ${JSON.parse(getToken()).access_token}`,
        ...headers,
      };
    }

    if (options.withDeviceId) {
      headers = {
        'X-Device-ID': await getDeviceId(),
        ...headers,
      };
    }

    requestOptions.payload = { ...requestOptions.payload, headers };

    if (options.withData) {
      requestOptions.payload = { ...requestOptions.payload, data };
    }

    if (options.withCredentials) {
      requestOptions.payload = { ...requestOptions.payload, withCredentials: true };
    }

    return request(this, method, newUrl, requestOptions);
  };
}

/* istanbul ignore next */
const createGenerator = (appName, endpoints) => {
  const logName = `frontend_dweb_${appName}`;
  const generateInterface = (method, endpointName, options = {}) => {
    const label = `bukareksa_${method}_${endpointName}_api`;
    const payload = { responseErrorJson: true };
    const url = endpoints[endpointName];
    const requestOptions = { payload, label, logName };

    if (options.withDeviceId) {
      return generateApiInterfaceAsync(method, url, requestOptions, options);
    }

    return generateApiInterface(method, url, requestOptions, options);
  };

  return generateInterface;
};

export default {
  createGenerator,
};
